import { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Building2, ChevronDown } from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// ============================================================================
// Types
// ============================================================================

interface AdminSummary {
  total_locations: number;
  incoming_messages: number;
  outgoing_messages: number;
  outgoing_segments: number;
  delivered_count: number;
  failed_count: number;
  delivery_rate: number;
  spam_filtered_count: number;
}

interface LocationSummary {
  location_id: string;
  location_name: string;
  outgoing_messages: number;
  outgoing_segments: number;
  incoming_messages: number;
  delivered_count: number;
  failed_count: number;
  delivery_rate: number;
}

interface DeliveryStatusItem {
  status: string;
  label: string;
  color: string;
  count: number;
  percentage: number;
}

interface MessageTrendPoint {
  time_bucket: string;
  outbound_messages: number;
  outbound_segments: number;
  inbound_messages: number;
  delivered_messages: number;
}

interface PeakUsagePoint {
  hour_of_day: number;
  day_of_week: number;
  message_count: number;
}

// API Response Types
interface SummaryResponse {
  success: boolean;
  summary?: AdminSummary;
}

interface LocationsResponse {
  success: boolean;
  locations?: LocationSummary[];
}

interface DeliveryStatusResponse {
  success: boolean;
  breakdown?: DeliveryStatusItem[];
}

interface MessageTrendResponse {
  success: boolean;
  trend?: MessageTrendPoint[];
  granularity?: string;
}

interface PeakUsageResponse {
  success: boolean;
  peakUsage?: PeakUsagePoint[];
}

interface ErrorBreakdownItem {
  category: string;
  label: string;
  color: string;
  count: number;
  percentage: number;
}

interface ErrorBreakdownResponse {
  success: boolean;
  breakdown?: ErrorBreakdownItem[];
  totalErrors?: number;
}

interface OptOutDataPoint {
  stat_date: string;
  opt_out_count: number;
  outbound_count: number;
  opt_out_rate: number;
}

interface OptOutStats {
  total_opt_outs: number;
  total_outbound: number;
  opt_out_rate: number;
  opt_outs_by_period: OptOutDataPoint[];
}

interface OptOutResponse {
  success: boolean;
  stats?: OptOutStats;
}

interface PipelineProgressionItem {
  state: string;
  label: string;
  color: string;
  count: number;
}

interface PipelineProgressionResponse {
  success: boolean;
  progression?: PipelineProgressionItem[];
  totalEvents?: number;
}

// ============================================================================
// Constants
// ============================================================================

const API_URL = import.meta.env.VITE_API_URL;
const AUTO_REFRESH_INTERVAL = 60000; // 60 seconds for admin

// Status colors for pie chart
const STATUS_COLORS: Record<string, string> = {
  delivered: '#22c55e',
  sent: '#3b82f6',
  failed: '#ef4444',
  undelivered: '#f59e0b',
  spam: '#8b5cf6',
  invalid: '#ec4899',
  landline: '#6366f1',
};

// Error category colors
const ERROR_COLORS: Record<string, string> = {
  spam: '#8b5cf6',
  invalid_number: '#ec4899',
  landline: '#6366f1',
  carrier: '#f59e0b',
  other: '#6b7280',
};

// Day names for heatmap
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ============================================================================
// Helper Functions
// ============================================================================

// Format a Date as a local YYYY-MM-DD string (for <input type="date">).
// Avoids toISOString() which would shift the displayed date by a day in non-UTC zones.
const toLocalDateString = (d: Date): string => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Parse a YYYY-MM-DD string as local midnight (matches what the picker shows).
const fromLocalDateString = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Format the picker's date range as the actual UTC window it covers, rendered in local time.
// The backend interprets the picker's YMD strings as inclusive UTC date filters
// (stat_date >= start AND stat_date <= end), so the real window is
// startYMD 00:00 UTC through endYMD 23:59:59 UTC.
const formatLocalRange = (startDate: Date, endDate: Date): string => {
  const utcStart = new Date(`${toLocalDateString(startDate)}T00:00:00Z`);
  const utcEnd = new Date(`${toLocalDateString(endDate)}T23:59:59Z`);
  const fmt = (d: Date): string => {
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const y = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${m}/${day}/${y} ${hh}:${mm}`;
  };
  return `${fmt(utcStart)} thru ${fmt(utcEnd)}`;
};

// Build a UTC-offset subtitle for charts that bucket by UTC hour.
// getTimezoneOffset() returns minutes WEST of UTC (positive = behind UTC), so we flip the sign.
const getUtcOffsetNote = (): string => {
  const offsetMin = -new Date().getTimezoneOffset();
  if (offsetMin === 0) {
    return 'Hours shown in UTC — your local time matches UTC';
  }
  const sign = offsetMin > 0 ? '+' : '−';
  const abs = Math.abs(offsetMin);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const offsetLabel = m === 0 ? `UTC${sign}${h}` : `UTC${sign}${h}:${String(m).padStart(2, '0')}`;
  const verb = offsetMin > 0 ? 'add' : 'subtract';
  const amount = m === 0 ? `${h} hour${h === 1 ? '' : 's'}` : `${h}h ${m}m`;
  return `Hours shown in UTC — your local time is ${offsetLabel} (${verb} ${amount} to convert)`;
};

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 14);
  return { start, end };
};

const includestoday = (endDate: Date) => {
  const today = new Date();
  return endDate.toDateString() === today.toDateString();
};

const formatDate = (dateStr: string, granularity: string) => {
  const date = new Date(dateStr);
  if (granularity === 'hourly') {
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' });
  }
  if (granularity === 'daily') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  if (granularity === 'weekly') {
    return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

// ============================================================================
// Admin SMS Dashboard Component
// ============================================================================

export default function SmsDashboard() {
  // Date range state
  const [startDate, setStartDate] = useState<Date>(() => getDefaultDateRange().start);
  const [endDate, setEndDate] = useState<Date>(() => getDefaultDateRange().end);

  // Location filter
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [locations, setLocations] = useState<LocationSummary[]>([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  // Data states
  const [summary, setSummary] = useState<AdminSummary | null>(null);
  const [deliveryStatus, setDeliveryStatus] = useState<DeliveryStatusItem[]>([]);
  const [messageTrend, setMessageTrend] = useState<MessageTrendPoint[]>([]);
  const [granularity, setGranularity] = useState<string>('daily');
  const [peakUsage, setPeakUsage] = useState<PeakUsagePoint[]>([]);
  const [errorBreakdown, setErrorBreakdown] = useState<ErrorBreakdownItem[]>([]);
  const [totalErrors, setTotalErrors] = useState(0);
  const [optOutStats, setOptOutStats] = useState<OptOutStats | null>(null);
  const [pipelineProgression, setPipelineProgression] = useState<PipelineProgressionItem[]>([]);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Auto-refresh ref
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ============================================================================
  // Data Fetching
  // ============================================================================

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);

    const params = new URLSearchParams({
      start: toLocalDateString(startDate),
      end: toLocalDateString(endDate),
    });

    if (selectedLocation !== 'all') {
      params.set('locationId', selectedLocation);
    }

    try {
      const [summaryRes, locationsRes, deliveryRes, trendRes, peakRes, errorRes, optOutRes, pipelineRes] = await Promise.all([
        fetch(`${API_URL}/admin/dashboard/summary?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/admin/dashboard/locations?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/admin/dashboard/delivery-status?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/admin/dashboard/message-trend?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/admin/dashboard/peak-usage?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/admin/dashboard/error-breakdown?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/admin/dashboard/opt-outs?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/admin/dashboard/pipeline-progression?${params}`, { credentials: 'include' }),
      ]);

      const [summaryData, locationsData, deliveryData, trendData, peakData, errorData, optOutData, pipelineData] = await Promise.all([
        summaryRes.json() as Promise<SummaryResponse>,
        locationsRes.json() as Promise<LocationsResponse>,
        deliveryRes.json() as Promise<DeliveryStatusResponse>,
        trendRes.json() as Promise<MessageTrendResponse>,
        peakRes.json() as Promise<PeakUsageResponse>,
        errorRes.json() as Promise<ErrorBreakdownResponse>,
        optOutRes.json() as Promise<OptOutResponse>,
        pipelineRes.json() as Promise<PipelineProgressionResponse>,
      ]);

      if (summaryData.success && summaryData.summary) {
        setSummary(summaryData.summary);
      }

      if (locationsData.success && locationsData.locations) {
        setLocations(locationsData.locations);
      }

      if (deliveryData.success && deliveryData.breakdown) {
        setDeliveryStatus(deliveryData.breakdown);
      }

      if (trendData.success) {
        setMessageTrend(trendData.trend || []);
        setGranularity(trendData.granularity || 'daily');
      }

      if (peakData.success) {
        setPeakUsage(peakData.peakUsage || []);
      }

      if (errorData.success) {
        setErrorBreakdown(errorData.breakdown || []);
        setTotalErrors(errorData.totalErrors || 0);
      }

      if (optOutData.success && optOutData.stats) {
        setOptOutStats(optOutData.stats);
      }

      if (pipelineData.success && pipelineData.progression) {
        setPipelineProgression(pipelineData.progression);
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [startDate, endDate, selectedLocation]);

  // Initial load
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh
  useEffect(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    if (includestoday(endDate)) {
      refreshIntervalRef.current = setInterval(() => {
        fetchData(true);
      }, AUTO_REFRESH_INTERVAL);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [endDate, fetchData]);

  // ============================================================================
  // Render Helpers
  // ============================================================================

  const formatChartData = () => {
    return messageTrend.map((point) => ({
      date: formatDate(point.time_bucket, granularity),
      outbound: point.outbound_messages,
      inbound: point.inbound_messages,
      delivered: point.delivered_messages,
    }));
  };

  const buildHeatmapData = () => {
    const grid: number[][] = Array(7).fill(null).map(() => Array(24).fill(0));
    peakUsage.forEach((point) => {
      if (point.day_of_week >= 0 && point.day_of_week < 7 && point.hour_of_day >= 0 && point.hour_of_day < 24) {
        grid[point.day_of_week][point.hour_of_day] = point.message_count;
      }
    });
    return grid;
  };

  const getHeatmapColor = (value: number, max: number) => {
    if (value === 0) return 'bg-gray-100';
    const intensity = Math.min(value / max, 1);
    if (intensity < 0.25) return 'bg-blue-100';
    if (intensity < 0.5) return 'bg-blue-300';
    if (intensity < 0.75) return 'bg-blue-500';
    return 'bg-blue-700';
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const heatmapData = buildHeatmapData();
  const maxHeatmapValue = Math.max(...heatmapData.flat());

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">SMS Analytics</h1>
        <div className="flex items-center gap-3">
          {/* Location Filter */}
          <div className="relative">
            <button
              onClick={() => setShowLocationDropdown(!showLocationDropdown)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Building2 className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">
                {selectedLocation === 'all' ? 'All Locations' : locations.find(l => l.location_id === selectedLocation)?.location_name || selectedLocation}
              </span>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>

            {showLocationDropdown && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowLocationDropdown(false)} />
                <div className="absolute top-full right-0 mt-1 w-64 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <button
                    onClick={() => { setSelectedLocation('all'); setShowLocationDropdown(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${selectedLocation === 'all' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                  >
                    All Locations
                  </button>
                  {locations.map((loc) => (
                    <button
                      key={loc.location_id}
                      onClick={() => { setSelectedLocation(loc.location_id); setShowLocationDropdown(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${selectedLocation === loc.location_id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                    >
                      {loc.location_name || loc.location_id}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={toLocalDateString(startDate)}
              onChange={(e) => setStartDate(fromLocalDateString(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={toLocalDateString(endDate)}
              onChange={(e) => setEndDate(fromLocalDateString(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          {/* Refresh Button */}
          <button
            onClick={() => fetchData()}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Local-time range note + Last Updated */}
      <div className="flex items-start justify-between gap-4 text-sm text-gray-500">
        <div>
          All data shown: {formatLocalRange(startDate, endDate)} in your local time
        </div>
        <div className="text-right whitespace-nowrap">
          Last updated: {lastUpdated.toLocaleTimeString()}
          {includestoday(endDate) && (
            <span className="ml-2 text-green-600">Auto-refreshing</span>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Total Locations</div>
          <div className="text-2xl font-bold text-gray-900">{summary?.total_locations || 0}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Outgoing Messages</div>
          <div className="text-2xl font-bold text-blue-600">{(summary?.outgoing_messages || 0).toLocaleString()}</div>
          <div className="text-xs text-gray-400">{(summary?.outgoing_segments || 0).toLocaleString()} segments</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Incoming Messages</div>
          <div className="text-2xl font-bold text-green-600">{(summary?.incoming_messages || 0).toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Delivery Rate</div>
          <div className="text-2xl font-bold text-purple-600">{(summary?.delivery_rate || 0).toFixed(1)}%</div>
          <div className="text-xs text-gray-400">
            {(summary?.delivered_count || 0).toLocaleString()} delivered, {(summary?.failed_count || 0).toLocaleString()} failed
          </div>
        </div>
        {(() => {
          const outgoing = summary?.outgoing_messages || 0;
          const spam = summary?.spam_filtered_count || 0;
          const spamRate = outgoing > 0 ? (spam / outgoing) * 100 : 0;
          const color = spamRate > 3 ? 'text-red-600' : spamRate > 1 ? 'text-yellow-600' : 'text-green-600';
          return (
            <div
              className="bg-white rounded-xl border border-gray-200 p-4"
              title="Messages filtered by carriers as spam (Twilio error 30007). A subset of the failed messages above."
            >
              <div className="text-sm text-gray-500">Spam Rate</div>
              <div className={`text-2xl font-bold ${color}`}>{spamRate.toFixed(2)}%</div>
              <div className="text-xs text-gray-400">{spam.toLocaleString()} filtered (30007)</div>
            </div>
          );
        })()}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Delivery Status Pie */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Delivery Status</h3>
          {deliveryStatus.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={deliveryStatus}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    dataKey="count"
                    nameKey="label"
                    label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                  >
                    {deliveryStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || '#6b7280'} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No delivery data available
            </div>
          )}
        </div>

        {/* Message Trend Line */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Message Activity</h3>
          {messageTrend.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={formatChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="outbound" name="Outbound" stroke="#3b82f6" strokeWidth={2} />
                  <Line type="monotone" dataKey="inbound" name="Inbound" stroke="#22c55e" strokeWidth={2} />
                  <Line type="monotone" dataKey="delivered" name="Delivered" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No trend data available
            </div>
          )}
        </div>
      </div>

      {/* Peak Usage Heatmap */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Peak Usage Times</h3>
          <p className="text-sm text-gray-500 mt-1">{getUtcOffsetNote()}</p>
        </div>
        {maxHeatmapValue > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="w-16"></th>
                  {Array.from({ length: 24 }, (_, i) => (
                    <th key={i} className="text-xs text-gray-500 font-normal py-1 w-8">
                      {i === 0 ? '12a' : i < 12 ? `${i}a` : i === 12 ? '12p' : `${i - 12}p`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {DAY_NAMES.map((day, dayIndex) => (
                  <tr key={day}>
                    <td className="text-xs text-gray-500 py-1">{day}</td>
                    {heatmapData[dayIndex].map((value, hour) => (
                      <td key={hour} className="p-0.5">
                        <div
                          className={`w-6 h-6 rounded ${getHeatmapColor(value, maxHeatmapValue)}`}
                          title={`${day} ${hour}:00 - ${value.toLocaleString()} messages`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center gap-2 mt-4 justify-end text-xs text-gray-500">
              <span>Less</span>
              <div className="w-4 h-4 bg-gray-100 rounded" />
              <div className="w-4 h-4 bg-blue-100 rounded" />
              <div className="w-4 h-4 bg-blue-300 rounded" />
              <div className="w-4 h-4 bg-blue-500 rounded" />
              <div className="w-4 h-4 bg-blue-700 rounded" />
              <span>More</span>
            </div>
          </div>
        ) : (
          <div className="h-48 flex items-center justify-center text-gray-500">
            No peak usage data available
          </div>
        )}
      </div>

      {/* Error Breakdown and Opt-Out Tracking Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Error Breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Error Breakdown</h3>
            <span className="text-sm text-gray-500">{totalErrors.toLocaleString()} total errors</span>
          </div>
          {errorBreakdown.length > 0 ? (
            <>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={errorBreakdown.map(item => ({ ...item, name: item.label, value: item.count }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                    >
                      {errorBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={ERROR_COLORS[entry.category] || '#6b7280'} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 border-t border-gray-100 pt-4 space-y-2">
                {errorBreakdown.map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: ERROR_COLORS[item.category] || '#6b7280' }}
                      />
                      <span className="text-gray-700">{item.label}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-gray-500">{item.count.toLocaleString()}</span>
                      <span className="text-gray-400 w-12 text-right">{item.percentage.toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-48 flex flex-col items-center justify-center text-gray-500">
              <div className="w-12 h-12 text-green-500 mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-lg font-medium text-green-600">No Errors</p>
              <p className="text-sm">All messages delivered successfully</p>
            </div>
          )}
        </div>

        {/* Opt-Out Tracking */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Opt-Out Tracking</h3>
          {optOutStats ? (
            <>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{optOutStats.total_opt_outs.toLocaleString()}</div>
                  <div className="text-xs text-gray-500">Total Opt-Outs</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{optOutStats.total_outbound.toLocaleString()}</div>
                  <div className="text-xs text-gray-500">Total Outbound</div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl font-bold ${optOutStats.opt_out_rate > 2 ? 'text-red-600' : optOutStats.opt_out_rate > 1 ? 'text-yellow-600' : 'text-green-600'}`}>
                    {optOutStats.opt_out_rate.toFixed(2)}%
                  </div>
                  <div className="text-xs text-gray-500">Opt-Out Rate</div>
                </div>
              </div>
              {optOutStats.opt_outs_by_period.length > 0 ? (
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={optOutStats.opt_outs_by_period.map(p => ({
                      date: new Date(p.stat_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                      optOuts: p.opt_out_count,
                      rate: p.opt_out_rate,
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="optOuts" name="Opt-Outs" stroke="#ef4444" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-40 flex items-center justify-center text-gray-500">
                  No opt-out trend data
                </div>
              )}
            </>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-500">
              No opt-out data available
            </div>
          )}
        </div>
      </div>

      {/* Pipeline Progression */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Pipeline Progression</h3>
          <span className="text-sm text-gray-500">
            {pipelineProgression.reduce((sum, item) => sum + item.count, 0).toLocaleString()} total events
          </span>
        </div>
        {pipelineProgression.some(item => item.count > 0) ? (
          <>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={pipelineProgression}
                  margin={{ top: 20, right: 20, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    angle={-25}
                    textAnchor="end"
                    height={60}
                    interval={0}
                  />
                  <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} allowDecimals={false} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                            <p className="font-medium text-gray-900">{data.label}</p>
                            <p className="text-sm text-gray-600">{data.count.toLocaleString()} events</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {pipelineProgression.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Legend with counts */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {pipelineProgression.map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <div
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-gray-600 truncate">{item.label}</span>
                  <span className="text-gray-900 font-medium ml-auto">{item.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="h-48 flex items-center justify-center text-gray-500">
            No pipeline data available for this period
          </div>
        )}
      </div>

      {/* Location Breakdown Table */}
      {selectedLocation === 'all' && locations.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Location Breakdown</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Outbound</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Segments</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Inbound</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Delivered</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Failed</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Delivery Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {locations.map((loc) => (
                  <tr
                    key={loc.location_id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedLocation(loc.location_id)}
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {loc.location_name || loc.location_id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 text-right">
                      {loc.outgoing_messages.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 text-right">
                      {loc.outgoing_segments.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 text-right">
                      {loc.incoming_messages.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-green-600 text-right">
                      {loc.delivered_count.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-red-600 text-right">
                      {loc.failed_count.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-right">
                      <span className={`font-medium ${loc.delivery_rate >= 95 ? 'text-green-600' : loc.delivery_rate >= 85 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {loc.delivery_rate.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
